'use client';

import { useIdentityContext } from '@cc-shopping/platform-analytics/IdentityContext';
import { ApprovalOddsErrorCategory } from '@cc-shopping/utils/errors/approvalOddsErrors';
import { datadogRum } from '@datadog/browser-rum';
import { visuallyHidden } from '@mui/utils';
import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { APPROVAL_ODDS_API_PATH } from 'src/app/api/credit-cards/og/approval-odds/config';
import type { ApprovalOddsResponse } from './constants';
export type ApprovalOddsActivationPhase = 'idle' | 'checking' | 'activated';
type ApprovalLikelihoodContextValue = {
  isLoading: boolean;
  approvalLikelihoods: ApprovalOddsResponse;
  activateApprovalLikelihoods: (approvalLikelihoods: ApprovalOddsResponse) => void;
  activationPhase: ApprovalOddsActivationPhase;
  setActivationPhase: (phase: ApprovalOddsActivationPhase) => void;
  bannerOpenRequestId: number;
  bannerOpenMarketplaceId?: string;
  requestBannerOpen: (marketplaceId?: string) => void;
};
const LIVE_STATUS: Record<ApprovalOddsActivationPhase, string> = {
  idle: '',
  checking: 'Checking approval odds',
  activated: 'Approval odds active'
};
const ApprovalLikelihoodContext = createContext<ApprovalLikelihoodContextValue>({
  isLoading: false,
  approvalLikelihoods: {},
  activateApprovalLikelihoods: () => undefined,
  activationPhase: 'idle',
  setActivationPhase: () => undefined,
  bannerOpenRequestId: 0,
  requestBannerOpen: () => undefined
});
type ApprovalLikelihoodProviderProps = {
  children: React.ReactNode;
  shouldFetchOnMount?: boolean;
};
export function ApprovalLikelihoodProvider({
  children,
  shouldFetchOnMount = true
}: ApprovalLikelihoodProviderProps) {
  const {
    isLoggedIn
  } = useIdentityContext();
  const [isLoading, setIsLoading] = useState(false);
  const [approvalLikelihoods, setApprovalLikelihoods] = useState<ApprovalOddsResponse>({});
  const [activationPhase, setActivationPhaseState] = useState<ApprovalOddsActivationPhase>('idle');
  const [bannerOpenRequestId, setBannerOpenRequestId] = useState(0);
  const [bannerOpenMarketplaceId, setBannerOpenMarketplaceId] = useState<string | undefined>();
  useEffect(() => {
    if (isLoggedIn && shouldFetchOnMount) {
      const fetchApprovalLikelihoods = async () => {
        try {
          setIsLoading(true);
          datadogRum.addAction('Approval odds fetch started', {
            approvalOddsUrl: APPROVAL_ODDS_API_PATH
          });
          const rawData = await fetch(APPROVAL_ODDS_API_PATH, {
            credentials: 'include'
          });
          if (!rawData.ok) {
            throw new Error(`Approval odds API returned status: ${rawData.status}`);
          }
          if (rawData.status === 204) {
            // No content is an expected response for users without a TU report.
            setApprovalLikelihoods({});
            return;
          }
          const response = (await rawData.json()) as ApprovalOddsResponse;
          setApprovalLikelihoods(response);
        } catch (err) {
          const error = new Error('Failed to fetch approval odds data', {
            cause: err
          });
          datadogRum.addError(error, {
            approvalOddsUrl: APPROVAL_ODDS_API_PATH,
            isLoggedIn: String(isLoggedIn),
            errorCategory: ApprovalOddsErrorCategory.FETCH_ERROR
          });
        } finally {
          setIsLoading(false);
        }
      };
      void fetchApprovalLikelihoods();
    }
  }, [isLoggedIn, shouldFetchOnMount]);
  const setActivationPhase = useCallback((phase: ApprovalOddsActivationPhase) => {
    setActivationPhaseState(current => current === 'activated' ? current : phase);
  }, []);
  const activateApprovalLikelihoods = useCallback((odds: ApprovalOddsResponse) => {
    setApprovalLikelihoods(odds);
    setActivationPhaseState('activated');
  }, []);
  const requestBannerOpen = useCallback((marketplaceId?: string) => {
    setBannerOpenMarketplaceId(marketplaceId);
    setBannerOpenRequestId(id => id + 1);
  }, []);
  const liveStatus = LIVE_STATUS[activationPhase];
  return <ApprovalLikelihoodContext.Provider value={{
    isLoading,
    approvalLikelihoods,
    activateApprovalLikelihoods,
    activationPhase,
    setActivationPhase,
    bannerOpenRequestId,
    bannerOpenMarketplaceId,
    requestBannerOpen
  }}>
      {liveStatus ? <div role="status" aria-live="polite" aria-atomic="true" style={visuallyHidden}>
          {liveStatus}
        </div> : null}
      {children}
    </ApprovalLikelihoodContext.Provider>;
}
export const useApprovalLikelihoodContext = () => {
  const context = useContext(ApprovalLikelihoodContext);
  if (!context) {
    console.warn("useApprovalLikelihoodContext must be used within an ApprovalLikelihoodProvider");
  }
  return context;
};